import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    TextField,
    Button,
    CircularProgress,
    Grid,
    FormControlLabel,
    Checkbox,
    Switch,
    Alert
} from '@mui/material';

// Full API bases
const API_ROUTE = 'https://figazuresql.swancreate.com/api/swa/AngelFund';
const MEDIA_UPLOAD_URL = 'https://media.swancreate.com/upload';
const IMAGE_BASE = 'https://media.swancreate.com/images/figfinance';

const AngelFundPage = () => {
    const [funds, setFunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        ProjectTitle: '',
        ProjectDescription: '',
        GoalAmount: '',
        ImageFile: null,
        isActive: true
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const fetchFunds = async () => {
        setLoading(true);
        try {
            const res = await fetch(API_ROUTE);
            if (!res.ok) throw new Error(`Error fetching funds: ${res.status}`);
            const data = await res.json();
            setFunds(data);
            setError(null);
        } catch (err) {
            console.error(err);
            setError('Unable to load Angel Funds. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFunds();
    }, []);

    const handleChange = e => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setForm(prev => ({ ...prev, ImageFile: files[0] }));
        } else if (type === 'checkbox') {
            setForm(prev => ({ ...prev, [name]: checked }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        let imageUrl = '';

        try {
            if (form.ImageFile) {
                const formData = new FormData();
                formData.append('folder', 'figfinance');
                formData.append('file', form.ImageFile);
                const uploadRes = await fetch(MEDIA_UPLOAD_URL, { method: 'POST', body: formData });
                if (!uploadRes.ok) throw new Error(`Image upload failed: ${uploadRes.status}`);
                // No URL returned, so use filename to construct path
                const fileName = form.ImageFile.name;
                imageUrl = `${IMAGE_BASE}/${fileName}`;
            }

            const newRecord = {
                ProjectTitle: form.ProjectTitle,
                ProjectDescription: form.ProjectDescription,
                GoalAmount: parseFloat(form.GoalAmount),
                ImageUrl: imageUrl,
                isActive: form.isActive
            };

            const res = await fetch(API_ROUTE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newRecord)
            });
            if (!res.ok) throw new Error(`Save failed: ${res.status}`);

            setForm({ ProjectTitle: '', ProjectDescription: '', GoalAmount: '', ImageFile: null, isActive: true });
            await fetchFunds();
        } catch (err) {
            console.error(err);
            setError('Submission failed. Please check your inputs and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (id, current) => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API_ROUTE}/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isActive: !current })
            });
            if (!res.ok) throw new Error(`Status update failed: ${res.status}`);
            await fetchFunds();
        } catch (err) {
            console.error(err);
            setError('Could not update status.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async id => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API_ROUTE}/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
            await fetchFunds();
        } catch (err) {
            console.error(err);
            setError('Could not delete entry.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Typography variant="h4" gutterBottom>
                Angel Funds
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} mb={4}>
                <TextField
                    name="ProjectTitle"
                    label="Title"
                    value={form.ProjectTitle}
                    onChange={handleChange}
                    fullWidth margin="normal" required
                />
                <TextField
                    name="ProjectDescription"
                    label="Description"
                    value={form.ProjectDescription}
                    onChange={handleChange}
                    fullWidth multiline rows={4} margin="normal" required
                />
                <TextField
                    name="GoalAmount"
                    label="Goal Amount"
                    type="number"
                    value={form.GoalAmount}
                    onChange={handleChange}
                    fullWidth margin="normal" required
                />
                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                    Upload Image
                    <input type="file" hidden name="ImageFile" accept="image/*" onChange={handleChange} />
                </Button>
                {form.ImageFile && (
                    <Box mt={2} mb={2}>
                        <Typography variant="subtitle2">Preview:</Typography>
                        <Box component="img" src={URL.createObjectURL(form.ImageFile)} alt="Preview" sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 1, mt: 1 }} />
                    </Box>
                )}
                <FormControlLabel control={<Checkbox name="isActive" checked={form.isActive} onChange={handleChange} />} label="Active" sx={{ ml: 2, mt: 2 }} />
                <Button type="submit" variant="contained" disabled={submitting} sx={{ ml: 2, mt: 2 }}>
                    {submitting ? 'Saving...' : 'Save'}
                </Button>
            </Box>

            <Grid container spacing={2}>
                {funds.map(f => (
                    <Grid item key={f.apiid} xs={12} md={6} lg={4}>
                        <Card>
                            {f.ImageUrl && (
                                <Box component="img" src={f.ImageUrl} alt={f.ProjectTitle} sx={{ width: '100%', height: 200, objectFit: 'cover' }} />
                            )}
                            <CardContent>
                                <Typography variant="h5" gutterBottom>{f.ProjectTitle}</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>{f.ProjectDescription}</Typography>
                                <Typography variant="subtitle1">Goal: ${f.GoalAmount.toFixed(2)}</Typography>
                                <Typography variant="caption" display="block">Created On: {new Date(f.CreatedOn).toLocaleDateString()}</Typography>
                            </CardContent>
                            <CardActions>
                                <FormControlLabel
                                    control={<Switch checked={f.isActive} onChange={() => handleToggleActive(f.apiid, f.isActive)} />}
                                    label={f.isActive ? 'Active' : 'Inactive'}
                                />
                                <Button color="error" onClick={() => handleDelete(f.apiid)}>Delete</Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default AngelFundPage;
